<template>
  <div>
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>契約回線一覧</span>
        <manual-button path="/nwinfo-contract/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-table density="compact" class="table-bordered my-4">
          <tbody>
            <tr>
              <th colspan="1" class="text-center">V番号</th>
              <td colspan="2">{{ userInfo.vnumber }}</td>
              <th colspan="2" class="text-center">VPNコード</th>
              <td colspan="1">{{ userInfo.vpnCode }}</td>
              <th colspan="2" class="text-center">メールアドレス</th>
              <td colspan="4">{{ userInfo.email }}</td>
            </tr>
          </tbody>
        </v-table>
        <v-row no-gutters>
          <v-col>
            <app-search
              :search="contractList.search"
              v-model="contractList.filter"
            />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn :href="downloadUrl" color="primary">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="contractList.sortBy"
          :fields="contractList.fields"
          :items="contractList.items"
          primary-key="contractDetailSeq"
          :filter="contractList.filter"
          :filter-option="contractList.filter_option"
          :hover="true"
          @click:row="contractLineDetails"
          sticky-header="50vh"
        >
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ContractLineReference from "@/modals/contractLine/ContractLineReference.vue";
import type {
  ContractLineGetEntity,
  ContractListEntity,
} from "@/apis/ContractLineApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "ContractLineList",
  data() {
    return {
      // 初期通信完了フラグ
      isLoaded: false,
      userInfo: {} as ContractLineGetEntity,
      contractList: {
        sortBy: {
          key: "enumber",
          order: "asc",
        } as SortItem,
        fields: [
          {
            key: "enumber",
            title: "回線番号",
            width: "170px",
            sortable: true,
          },
          {
            key: "contractor",
            title: "契約者名",
            width: "200px",
            sortable: true,
          },
          {
            key: "endUserName",
            title: "エンドユーザ名",
            width: "200px",
            sortable: true,
          },
          {
            key: "office",
            title: "事業所名(拠点名)",
            width: "200px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (value: string, _k, item: ContractListEntity) => {
              return item.accessType === "REMOTE_ACCESS_GW_FRE" ||
                item.accessType === "REMOTE_ACCESS_GW_CPA" ||
                item.accessType === "REMOTE_ACCESS_GW_CRG" ||
                item.accessType === "REMOTE_ACCESS_GW2_FRE" ||
                item.accessType === "REMOTE_ACCESS_GW2_CPA" ||
                item.accessType === "REMOTE_ACCESS_GW2_CRG"
                ? "-"
                : value;
            },
          },
          {
            key: "accessType",
            title: "メニュー",
            width: "300px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "contractLineAccessType"),
          },
          {
            key: "startEndDate",
            title: "サービス開廃日",
            width: "200px",
            sortable: true,
            sortByFormatted: true,
            formatter: (value: string) => this.$filter.date(value),
          },
          {
            key: "isSecurityOption",
            title: "セキュリティ監視契約",
            width: "250px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) => {
              return this.$filter.enumTo(value, "contractLineIsSecurityOption");
            },
          },
        ] as AppTableHeader<ContractListEntity>[],
        items: [] as ContractListEntity[],
        search: {
          enumber: { type: "text", label: "回線番号" },
          contractor: { type: "text", label: "契約者名" },
          endUserName: { type: "text", label: "エンドユーザ名" },
          office: { type: "text", label: "事業所名(拠点名)" },
          accessType: {
            type: "select",
            label: "メニュー",
            items: accessTypeItems.map((v) => {
              return {
                text: this.$filter.enumTo(v, "contractLineAccessType"),
                value: v,
              };
            }),
          },
          isSecurityOption: {
            type: "select",
            label: "セキュリティ監視契約",
            items: isSecurityOptionItems,
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          enumber: "like",
          contractor: "like",
          endUserName: "like",
          office: "like",
          accessType: "equal",
          isSecurityOption: "equal",
        } as FilterOption,
      },
      isSecurityOptionItems: isSecurityOptionItems,
      accessTypeItems: accessTypeItems,
      downloadUrl: this.$api.contractLine.getCsvURL(),
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** 一覧を取得 */
    async load() {
      // 契約回線情報取得APIを呼ぶ
      this.userInfo = await this.$api.contractLine.getAll();
      this.contractList.items = this.userInfo.contractList;
    },

    /** 契約回線情報モーダル */
    contractLineDetails(item: ContractListEntity) {
      this.$modal.show(ContractLineReference, {
        contractReference: item,
      });
    },
  },
});

/** 契約回線種別の定数 */
/** 「CPA 5GSA」ご用意してない理由は「CPA 5GSA」は10月リリースに入らないので、フィルターの選択肢からは削除 */
export const accessTypeItems: ContractListEntity["accessType"][] = [
  "INTERNET",
  "ETHERNET_L2",
  "ETHERNET_L3",
  "BROADBAND_ACCESS2_L2",
  "BROADBAND_ACCESS2_L3",
  "WIRELESS_ACCESS_L2",
  "WIRELESS_ACCESS_L3",
  "REMOTE_ACCESS_GW_FRE",
  "REMOTE_ACCESS_GW_CPA",
  "REMOTE_ACCESS_GW_CRG",
  "PLATFORM_GATEWAY",
  "PF_ACCESS_GW",
  "EXTEND_ETHERNET",
  "IPSEC",
  "ETHERNET2",
  "ETHERNET2_PF",
  "BROADBAND_ACCESS3",
  "WIRELESS_ACCESS2",
  "PF_ACCESS_GW2",
  "VN_L2L3_CONNECT",
  "REMOTE_ACCESS_GW2_FRE",
  "REMOTE_ACCESS_GW2_CPA",
  "REMOTE_ACCESS_GW2_CRG",
  "ETHERNET_LIGHT",
  "MULTI_CLOUD_GW",
  "CPA_5GSA",
];

export const isSecurityOptionItems: { value: boolean; text: string }[] = [
  { value: true, text: "有り" },
  { value: false, text: "無し" },
];
</script>

<style lang="scss" scoped>
.v-table th {
  background-color: #e9ecef;
}
</style>
